<template>
    <v-container fluid class="player-page">
        <template v-if="!player.id">
            <v-skeleton-loader type="image" height="280" class="rounded-lg mb-4" />
            <v-skeleton-loader type="article" class="rounded-lg" />
        </template>

        <template v-else>
            <!-- ---------- Hero ---------- -->
            <section
                class="player-hero"
                :class="{ 'player-hero--mobile': $vuetify.breakpoint.xs }"
            >
                <div class="player-hero__photo">
                    <img v-if="!player.freeAgent && player.team && player.team.logo" :src="`${assetsUrl}/${player.team.logo}`" class="player-hero__watermark" alt="">
                    <img
                        :src="player.image ? `${assetsUrl}/${player.image}` : defaultPlayerImg"
                        class="player-hero__img"
                        alt=""
                        @error="$event.target.src = defaultPlayerImg"
                    >
                </div>

                <div class="player-hero__info">
                    <router-link v-if="player.freeAgent" :to="{ name: 'FreeAgents' }" class="player-hero__team">
                        <v-icon x-small color="white">mdi-account-search-outline</v-icon>
                        Free Agent
                    </router-link>
                    <router-link
                        v-else-if="player.team && player.team.id"
                        :to="{ name: 'Team', params: { id: player.team.id } }"
                        class="player-hero__team"
                    >
                        <img v-if="player.team.logo" :src="`${assetsUrl}/${player.team.logo}`" alt="">
                        {{ player.team.name }}
                    </router-link>
                    <h1 class="player-hero__name">{{ player.name }} {{ player.surname }}</h1>
                    <div class="player-hero__chips">
                        <span v-if="player.jersey !== null && player.jersey !== undefined" class="player-chip player-chip--jersey">#{{ player.jersey }}</span>
                        <span v-if="player.position" class="player-chip">{{ positionLabel }}</span>
                        <span v-if="player.coach" class="player-chip">Coach</span>
                        <span v-if="player.leader" class="player-chip player-chip--leader">
                            <v-icon x-small color="white">mdi-star</v-icon>
                            Αρχηγός
                        </span>
                    </div>

                    <!-- the athlete's health card, filled in, to print for the doctor -->
                    <health-card-button :player="player" />

                    <div class="player-hero__facts">
                        <div v-if="heightCm" class="player-fact">
                            <span class="player-fact__value">{{ heightCm }}<small>cm</small></span>
                            <span class="player-fact__label">Ύψος</span>
                        </div>
                        <div v-if="age" class="player-fact">
                            <span class="player-fact__value">{{ age }}</span>
                            <span class="player-fact__label">Ηλικία · {{ player.birthday }}</span>
                        </div>
                        <div v-if="player.freeAgent" class="player-fact">
                            <span class="player-fact__value">{{ typeStats.length }}</span>
                            <span class="player-fact__label">Αγώνες (σύνολο)</span>
                        </div>
                        <div v-else-if="type !== 'cup' && type !== 'playoffs' && noOfMatches" class="player-fact" :class="eligibleGames < noOfMatches ? 'player-fact--bad' : 'player-fact--good'">
                            <span class="player-fact__value">{{ eligibleGames }}<small>/{{ noOfMatches }}</small></span>
                            <span class="player-fact__label">Αγώνες</span>
                        </div>
                        <div v-if="!player.freeAgent && type !== 'cup' && type !== 'playoffs'" class="player-fact" :class="player.doctorCheck ? 'player-fact--good' : 'player-fact--bad'">
                            <v-icon :color="player.doctorCheck ? 'green darken-2' : 'red darken-2'">
                                {{ player.doctorCheck ? 'mdi-check-circle' : 'mdi-close-circle' }}
                            </v-icon>
                            <span class="player-fact__label">Χαρτί γιατρού</span>
                        </div>
                    </div>

                    <div class="player-market">
                        <div class="player-market__head">
                            <span class="player-market__label">Market value</span>
                            <span class="player-market__value">{{ Number(player.averagePir || 0).toFixed(2) }}</span>
                            <v-icon v-if="marketTrend === 'up'" small color="green">mdi-chevron-double-up</v-icon>
                            <v-icon v-else-if="marketTrend === 'down'" small color="red">mdi-chevron-double-down</v-icon>
                            <v-icon v-else-if="marketTrend === 'equal'" small color="grey">mdi-arrow-left-right-bold</v-icon>
                        </div>
                        <v-sparkline
                            v-if="player.pirDates && player.pirDates.length > 1"
                            :value="player.pirDates.map(element => element.pir)"
                            :gradient="sparkline.gradient"
                            :smooth="sparkline.radius || false"
                            :padding="sparkline.padding"
                            :line-width="sparkline.width"
                            :stroke-linecap="sparkline.lineCap"
                            :gradient-direction="sparkline.gradientDirection"
                            :fill="sparkline.fill"
                            :type="sparkline.type"
                            :auto-line-width="sparkline.autoLineWidth"
                            height="40"
                            auto-draw
                        />
                    </div>
                </div>

                <div v-if="topStats" class="player-hero__radar">
                    <radar
                        :player-data="getAverageStats()"
                        :top-stats="topStats"
                        class="player-radar"
                        :style="{ backgroundColor: teamColor }"
                    />
                </div>
            </section>

            <!-- ---------- Achievements ---------- -->
            <section v-if="badgeList.length" class="player-section">
                <div class="player-section__title">
                    <span class="player-section__bar custom-orange" />
                    BIG Achievements
                </div>
                <div class="player-badges">
                    <info-tip v-for="badge in badgeList" :key="badge.name" bottom>
                        <div class="player-badge" :class="{ 'player-badge--locked': !badge.level }">
                            <img :src="badge.src" alt="">
                        </div>
                        <template #content>
                            <span>{{ badge.description }}{{ badge.level ? '' : ' (δεν έχει κατακτηθεί)' }}</span>
                        </template>
                    </info-tip>
                </div>
            </section>

            <!-- ---------- Phase + tabs ---------- -->
            <div class="player-nav">
                <div v-if="player.freeAgent" class="player-all-matches">
                    <v-icon small color="orange darken-2">mdi-history</v-icon>
                    Όλοι οι αγώνες · όλες οι σεζόν και οι ομάδες
                </div>
                <match-type-choice v-else :current-type="type" @setType="setType" />
                <v-tabs v-model="tab" centered class="player-tabs">
                    <v-tabs-slider class="custom-orange" />
                    <v-tab v-for="(item, index) in tabItems" :key="index">{{ item }}</v-tab>
                </v-tabs>
            </div>

            <template v-if="tab === 0">
                <player-stats :total-stats="getCummulativeStats()" :avg-stats="getAverageStats()" />

                <section v-if="shootingCharts.length" class="player-section">
                    <scroll-strip :gap="12">
                        <template #title>
                            <div class="player-section__title">
                                <span class="player-section__bar custom-orange" />
                                Shooting charts
                            </div>
                        </template>
                        <v-card
                            v-for="(stat, index) in shootingCharts"
                            :key="`stat-${index}`"
                            class="shooting-card rounded-lg"
                            @click="$router.push({ name: 'Match', params: { id: stat.matchId } })"
                        >
                            <div class="shooting-card__head">
                                <img v-if="stat.opponentTeamLogo" :src="`${assetsUrl}/${stat.opponentTeamLogo}`" alt="">
                                <div>
                                    <div class="shooting-card__opponent">vs {{ stat.opponentTeamName }}</div>
                                    <div class="shooting-card__date">{{ formatDate(stat.matchDatetime) }}</div>
                                </div>
                            </div>
                            <shot-summary :stat="stat">
                                <span class="shooting-card__ts">
                                    TS <b :class="tsClass(tsPctForStat(stat))">{{ tsPctForStat(stat) }}%</b>
                                    <info-tip bottom max-width="380" content-class="ts-tooltip-surface">
                                        <v-icon x-small class="ts-info-icon" color="orange">mdi-information-outline</v-icon>
                                        <template #content>
                                            <div class="ts-tooltip">
                                                <div class="font-weight-bold mb-1">True Shooting Percentage (TS%)</div>
                                                <div class="mb-2">TS% = Points / (2 x (Field Goal Attempts + 0.44 x Free Throw Attempts))</div>
                                                <div class="ts-poor">Below 50%: Poor scoring efficiency</div>
                                                <div class="ts-below-average">50% to below 55%: Below average</div>
                                                <div class="ts-average">55%: Solid and league average</div>
                                                <div class="ts-very-good">57% to 58%: Very good / highly efficient</div>
                                                <div class="ts-elite">60% and above: Elite</div>
                                            </div>
                                        </template>
                                    </info-tip>
                                </span>
                            </shot-summary>
                            <shooting-chart :shots="stat.throwPositions" :color="teamColor" />
                        </v-card>
                    </scroll-strip>
                </section>

                <section class="player-section">
                    <div class="player-section__title">
                        <span class="player-section__bar custom-orange" />
                        Αγώνες
                    </div>
                    <game-stats :stats="sortedStatsByDate" :show-team="!!player.freeAgent" />
                </section>
            </template>
            <player-records v-else-if="tab === 1" :stats="getMaxStats()" :team="player.team ? player.team.name : ''" />
            <player-moments v-else :moments="player.highlights" />
        </template>
    </v-container>
</template>

<script>
    import PlayerStats from '@/components/PlayerStats'
    import ShootingChart from '@/components/ShootingChart'
    import ShotSummary from '@/components/ShotSummary'
    import ScrollStrip from '@/components/ScrollStrip'
    import PlayerMoments from '@/components/PlayerMoments'
    import GameStats from '@/components/GameStats'
    import PlayerRecords from '@/components/PlayerRecords'
    import playerService from '@/services/player'
    import configService from '@/services/config'
    import MatchTypeChoice from '@/components/MatchTypeChoice'
    import Radar from '@/components/Radar.vue'
    import HealthCardButton from '@/components/HealthCardButton'
    import moment from 'moment'
    import { foulsOf } from '@/utils/playerStats'

    const POSITION_LABELS = { guard: 'Guard', forward: 'Forward', center: 'Center' }

    const gradients = [
        ['#ffd200'],
        ['#ffd200'],
        ['red', 'orange', 'yellow'],
        ['purple', 'violet'],
        ['#ffd200', '#F0F', '#FF0'],
        ['#f72047', '#ffd200', '#ffd200']
    ]

    export default {
        components: { PlayerStats, GameStats, PlayerRecords, PlayerMoments, MatchTypeChoice, Radar, ShootingChart, ShotSummary, ScrollStrip, HealthCardButton },
        data() {
            return {
                player: {},
                items: ['Στατιστικά', 'Ρεκόρ'],
                tab: null,
                assetsUrl: 'https://storage.googleapis.com',
                noOfMatches: 0,
                type: 'regular_season',
                matchTypes: ['placements', 'regular_season', 'playoffs', 'cup'],
                badges: [
                    {
                        name: 'participation',
                        gold: { src: require('@/assets/badges/attend_golden.svg') },
                        silver: { src: require('@/assets/badges/attend_golden.svg') },
                        bronze: { src: require('@/assets/badges/attend_golden.svg') },
                        description: 'Συμμετοχές σε αγώνα'
                    },
                    {
                        name: 'doubleDouble',
                        gold: { src: require('@/assets/badges/double_golden.svg') },
                        silver: { src: require('@/assets/badges/double_silver.svg') },
                        bronze: { src: require('@/assets/badges/double_bronze.svg') },
                        description: 'Double Double σε αγώνες'
                    },
                    {
                        name: 'tripleDouble',
                        gold: { src: require('@/assets/badges/triple_golden.svg') },
                        silver: { src: require('@/assets/badges/triple_silver.svg') },
                        bronze: { src: require('@/assets/badges/triple_bronze.svg') },
                        description: 'Triple Double σε αγώνες'
                    },
                    {
                        name: 'points',
                        gold: { src: require('@/assets/badges/point_golden.svg') },
                        silver: { src: require('@/assets/badges/point_silver.svg') },
                        bronze: { src: require('@/assets/badges/point_bronze.svg') },
                        description: 'Πόντοι σε αγώνες'
                    },
                    {
                        name: 'rebounds',
                        gold: { src: require('@/assets/badges/rebound_golden.svg') },
                        silver: { src: require('@/assets/badges/rebound_silver.svg') },
                        bronze: { src: require('@/assets/badges/rebound_bronze.svg') },
                        description: 'Ριμπάουντ σε αγώνες'
                    },
                    {
                        name: 'steals',
                        gold: { src: require('@/assets/badges/thief_golden.svg') },
                        silver: { src: require('@/assets/badges/thief_silver.svg') },
                        bronze: { src: require('@/assets/badges/thief_bronze.svg') },
                        description: 'Κλεψίματα σε αγώνες'
                    }
                ],
                sparkline: {
                    width: 1,
                    radius: 10,
                    padding: 8,
                    lineCap: 'round',
                    gradient: gradients[5],
                    gradientDirection: 'top',
                    gradients,
                    fill: false,
                    type: 'trend',
                    autoLineWidth: false
                },
                topStats: null,
                defaultPlayerImg: require('@/assets/default_player.png'),
                activePhasePresetApplied: false
            }
        },
        computed: {
            // Matches of the selected phase; one filter for the averages, the totals, the records and the game list
            typeStats() {
                if (!Array.isArray(this.player.stats)) {
                    return []
                }
                // a free agent: every match of any season and team (see the backend)
                if (this.player.freeAgent) {
                    return this.player.stats
                }
                return this.player.stats.filter(element => (this.type === 'cup' ? element.cup : (element.phase === this.type && !element.cup)))
            },
            sortedStatsByDate() {
                return [...this.typeStats].sort((a, b) => new Date(b.matchDatetime) - new Date(a.matchDatetime))
            },
            shootingCharts() {
                return this.sortedStatsByDate.filter(stat => Array.isArray(stat.throwPositions) && stat.throwPositions.length)
            },
            tabItems() {
                return this.player.highlights ? this.items.concat('BIG Moments') : this.items
            },
            teamColor() {
                return (this.player.team && this.player.team.color) || '#ff7133'
            },
            positionLabel() {
                return POSITION_LABELS[this.player.position] || this.player.position
            },
            // Heights are stored either in metres (1.85) or centimetres (185)
            heightCm() {
                const value = Number(this.player.height)
                if (!value) return null
                return Math.round(value < 3 ? value * 100 : value)
            },
            age() {
                if (!this.player.birthday) return null
                const birthday = moment(this.player.birthday, 'DD/MM/YYYY')
                return birthday.isValid() ? moment().diff(birthday, 'years') : null
            },
            eligibleGames() {
                return (this.player.stats || []).filter(element => ((element.phase === 'regular_season' || element.phase === 'placements') && !element.cup)).length
            },
            marketTrend() {
                const dates = this.player.pirDates || []
                if (dates.length < 2) return null
                const previous = dates.slice(0, -1)
                const previousAverage = previous.reduce((sum, element) => sum + element.pir, 0) / previous.length
                if (this.player.averagePir > previousAverage) return 'up'
                if (this.player.averagePir < previousAverage) return 'down'
                return 'equal'
            },
            badgeList() {
                const earned = this.player.badges || {}
                return Object.keys(earned).map(name => {
                    const badge = this.badges.find(element => element.name === name)
                    if (!badge) return null
                    const level = earned[name]
                    return {
                        name,
                        level,
                        description: badge.description,
                        src: (level && badge[level] ? badge[level] : badge.bronze).src
                    }
                }).filter(Boolean)
            },
            activePhase() {
                const config = this.$store.getters.getConfig
                return config && config.activePhase ? config.activePhase : null
            }
        },
        watch: {
            '$route.params.id': {
                handler() {
                    this.getPlayer(this.$route.params.id)
                }
            },
            'player.noOfMatches': {
                handler() {
                    this.getNoOfMatches()
                },
                deep: true
            },
            activePhase: {
                handler() {
                    this.applyActivePhasePreset()
                }
            },
            type: {
                handler() {
                    this.getPlayer(this.$route.params.id)
                    this.getTopStats()
                }
            }
        },
        created() {
            const presetApplied = this.applyActivePhasePreset()
            if (!presetApplied) {
                this.getPlayer(this.$route.params.id)
                this.getTopStats()
            }
        },
        methods: {
            applyActivePhasePreset() {
                if (this.activePhasePresetApplied) {
                    return false
                }

                if (!this.activePhase || !this.matchTypes.includes(this.activePhase) || this.type !== 'regular_season') {
                    return false
                }

                this.activePhasePresetApplied = true

                if (this.activePhase === this.type) {
                    return false
                }

                this.type = this.activePhase
                return true
            },
            formatDate(date) {
                return date ? moment(date).format('DD/MM/YYYY') : ''
            },
            pct(made, attempts) {
                return attempts ? Math.round(made / attempts * 100) : 0
            },
            setType(option) {
                if (typeof option === 'string' && this.matchTypes.includes(option)) {
                    this.type = option
                    return
                }

                if (typeof option === 'number' && this.matchTypes[option]) {
                    this.type = this.matchTypes[option]
                }
            },
            getPlayer(playerId) {
                playerService.getPlayer(playerId).then(resp => {
                    this.player = resp.data
                    this.setPlayerPageMeta(this.player)
                })
            },
            upsertMetaTag(selector, attrs) {
                let el = document.head.querySelector(selector)
                if (!el) {
                    el = document.createElement('meta')
                    document.head.appendChild(el)
                }

                Object.keys(attrs).forEach(key => {
                    el.setAttribute(key, attrs[key])
                })
            },
            getPlayerMetaImage(player = {}) {
                const raw = player.image || player.photo || player.avatar || null
                if (!raw) return `${window.location.origin}/icon-512.png`
                if (/^https?:\/\//i.test(raw)) return raw
                return `${this.assetsUrl}/${raw}`
            },
            setPlayerPageMeta(player = {}) {
                const siteName = 'BIG Basketball'
                const firstName = String(player.name || '').trim()
                const lastName = String(player.surname || '').trim()
                const fullName = [firstName, lastName].filter(Boolean).join(' ').trim() || 'Παίκτης'
                const teamName = player.freeAgent ? 'Free Agent' : String(player?.team?.name || '').trim()
                const title = teamName
                    ? `${fullName} | ${teamName} | ${siteName}`
                    : `${fullName} | ${siteName}`
                const description = teamName
                    ? `Δείτε το προφίλ του ${fullName} στην ομάδα ${teamName}: στατιστικά, επιδόσεις και πληροφορίες παίκτη.`
                    : `Δείτε το προφίλ του ${fullName}: στατιστικά, επιδόσεις και πληροφορίες παίκτη.`
                const image = this.getPlayerMetaImage(player)
                const url = window.location.href

                document.title = title

                this.upsertMetaTag('meta[name="description"]', { name: 'description', content: description })
                this.upsertMetaTag('meta[property="og:site_name"]', { property: 'og:site_name', content: siteName })
                this.upsertMetaTag('meta[property="og:title"]', { property: 'og:title', content: title })
                this.upsertMetaTag('meta[property="og:description"]', { property: 'og:description', content: description })
                this.upsertMetaTag('meta[property="og:image"]', { property: 'og:image', content: image })
                this.upsertMetaTag('meta[property="og:url"]', { property: 'og:url', content: url })
                this.upsertMetaTag('meta[property="og:type"]', { property: 'og:type', content: 'profile' })
                this.upsertMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary' })
                this.upsertMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
                this.upsertMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: description })
                this.upsertMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: image })
            },
            getTopStats() {
                playerService.getTopStats().then(resp => {
                    this.topStats = resp.data
                })
            },
            getCummulativeStats() {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                this.typeStats.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })
                return stats
            },
            getAverageStats() {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                const games = this.typeStats
                games.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })

                Object.keys(stats).forEach(element => {
                    stats[element] = games.length ? Math.round(stats[element] / games.length * 10) / 10 : 0
                })

                return stats
            },
            getMaxStats() {
                const stats = {
                    points: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'points'
                    },
                    rebounds: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'reb.'
                    },
                    assists: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'assists'
                    },
                    steals: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'steals'
                    },
                    turnovers: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'turnov.'
                    },
                    blockedShots: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'blocks'
                    },
                    twoPointsMade: {
                        value: 0,
                        oponnent: null,
                        date: null
                    },
                    threePointsMade: {
                        value: 0,
                        oponnent: null,
                        date: null
                    },
                    freeThrowsMade: {
                        value: 0,
                        oponnent: null,
                        date: null
                    }
                }
                this.typeStats.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        if (element[key] >= stats[key].value) {
                            stats[key] = {
                                matchId: element.matchId,
                                value: element[key],
                                oponnent: element.opponentTeamName,
                                // a free agent played for several teams
                                team: element.teamName,
                                date: element.matchDatetime
                            }
                        }
                    }
                })

                return stats
            },
            getNoOfMatches() {
                configService.getConfigField(this.player.noOfMatches).then(resp => {
                    this.noOfMatches = resp.data[this.player.noOfMatches]
                })
            },
            tsPctForStat(stat = {}) {
                const points = Number(stat.points) || 0
                const fga = (Number(stat.twoPointAttempts) || 0) + (Number(stat.threePointAttempts) || 0)
                const fta = Number(stat.freeThrowAttempts) || 0
                const denominator = 2 * (fga + (0.44 * fta))
                if (denominator === 0) return (0).toFixed(1)
                return ((points / denominator) * 100).toFixed(1)
            },
            tsClass(tsValue) {
                const ts = Number(tsValue)
                if (!Number.isFinite(ts)) return 'ts-poor'
                if (ts < 50) return 'ts-poor'
                if (ts < 55) return 'ts-below-average'
                if (ts < 57) return 'ts-average'
                if (ts < 60) return 'ts-very-good'
                return 'ts-elite'
            }

        }
    }
</script>

<style scoped>
/* a free agent: no phase choice, every match together */
.player-all-matches {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 8px;
    background: #fff3e0;
    color: #e65100;
    font-weight: 700;
    font-size: 0.9rem;
}

.player-page {
    max-width: 1224px;
    margin: 0 auto;
    font-family: Ubuntu, Arial, sans-serif;
    text-align: left;
}

/* ---------- Hero ---------- */
.player-hero {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 20px 24px;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
    overflow: hidden;
}

.player-hero__photo {
    position: relative;
    flex: 0 0 220px;
    width: 220px;
    height: 260px;
}

.player-hero__watermark {
    position: absolute;
    top: 50%;
    left: 0;
    height: 130%;
    width: auto;
    max-width: none;
    transform: translate(-20%, -50%);
    opacity: 0.12;
    pointer-events: none;
}

.player-hero__img {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.25));
}

.player-hero__info {
    flex: 1 1 auto;
    min-width: 0;
}

.player-hero__team {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px 2px 3px;
    border-radius: 14px;
    background: #0d0d0f;
    color: #fff !important;
    font-size: 0.78rem;
    font-weight: 700;
    text-decoration: none;
}

.player-hero__team img {
    width: 22px;
    height: 22px;
    padding: 1px;
    border-radius: 50%;
    background: #fff;
    object-fit: contain;
}

.player-hero__name {
    margin-top: 6px;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 2.5rem;
    font-weight: 800;
    line-height: 1.1;
    word-break: break-word;
}

.player-hero__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.player-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 10px;
    border-radius: 6px;
    background: #f1f1f3;
    font-size: 0.8rem;
    font-weight: 700;
}

.player-chip--jersey {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 50%, orange 100%);
    color: #fff;
}

.player-chip--leader {
    background: #0d0d0f;
    color: #fff;
}

.player-hero__facts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
}

.player-fact {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 76px;
    padding: 6px 12px;
    border-radius: 8px;
    background: #f4f4f6;
}

.player-fact__value {
    font-size: 1.3rem;
    font-weight: 800;
    line-height: 1.1;
}

.player-fact__value small {
    margin-left: 2px;
    font-size: 0.7rem;
}

.player-fact__label {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
}

.player-fact--good {
    box-shadow: inset 0 -3px 0 #2e7d32;
}

.player-fact--bad {
    box-shadow: inset 0 -3px 0 #c62828;
}

.player-market {
    max-width: 420px;
    margin-top: 14px;
}

.player-market__head {
    display: flex;
    align-items: center;
    gap: 8px;
}

.player-market__label {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.player-market__value {
    font-size: 1.2rem;
    font-weight: 800;
}

.player-hero__radar {
    flex: 0 0 auto;
}

.player-radar {
    width: 320px !important;
    height: 320px !important;
    padding: 8px;
    border-radius: 16px;
}

/* ---------- Sections ---------- */
.player-section {
    margin-top: 20px;
}

.player-section__title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    padding: 8px 14px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-size: 1.1rem;
    font-weight: 800;
}

.player-section__bar {
    width: 5px;
    height: 20px;
    border-radius: 2px;
}

.player-section__actions {
    margin-left: auto;
}

/* ---------- Badges ---------- */
.player-badges {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 10px;
    padding: 14px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.player-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 110px;
}

.player-badge img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.player-badge--locked {
    opacity: 0.3;
    filter: grayscale(1);
}

/* ---------- Phase + tabs ---------- */
.player-nav {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
    padding: 12px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.player-tabs {
    width: 100%;
}

.v-tab {
    text-transform: none !important;
    font-weight: bolder;
    font-size: 15px;
    color: #757578 !important;
}

.v-tab::before {
    opacity: 0;
    pointer-events: none;
    position: absolute;
    background-color: transparent;
}

.v-tab--active {
    background: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

/* ---------- Shooting charts ---------- */

.shooting-card {
    flex: 0 0 auto;
    padding: 12px;
    cursor: pointer;
}

.shooting-card__head {
    display: flex;
    align-items: center;
    gap: 10px;
}

.shooting-card__head img {
    width: 40px;
    height: 40px;
    object-fit: contain;
}

.shooting-card__opponent {
    font-weight: 800;
}

.shooting-card__date {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.8rem;
}

.shooting-card__ts {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.82rem;
}

/* ---------- TS% ---------- */
.ts-info-icon {
    cursor: pointer;
}

.ts-tooltip {
    font-size: 13px;
    line-height: 1.4;
}

::v-deep .v-tooltip__content.ts-tooltip-surface {
    background: #fff !important;
    opacity: 1 !important;
    color: #111;
    border: 1px solid #d7dbe3;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.ts-poor {
    color: #d32f2f;
}

.ts-below-average {
    color: #ef6c00;
}

.ts-average {
    color: #f9a825;
}

.ts-very-good {
    color: #2e7d32;
}

.ts-elite {
    color: #1b5e20;
}

/* ---------- Responsive ---------- */
@media (max-width: 1100px) {
    .player-hero {
        flex-wrap: wrap;
    }

    .player-hero__radar {
        width: 100%;
        display: flex;
        justify-content: center;
    }
}

@media (max-width: 600px) {
    .player-hero--mobile {
        flex-direction: column;
        gap: 12px;
        padding: 16px 14px;
        text-align: center;
    }

    .player-hero--mobile .player-hero__photo {
        flex-basis: auto;
        width: 170px;
        height: 200px;
    }

    .player-hero--mobile .player-hero__chips,
    .player-hero--mobile .player-hero__facts {
        justify-content: center;
    }

    .player-hero--mobile .player-market {
        margin-left: auto;
        margin-right: auto;
    }

    .player-hero--mobile .player-market__head {
        justify-content: center;
    }

    .player-hero__name {
        font-size: 1.8rem;
    }

    .player-radar {
        width: 260px !important;
        height: 260px !important;
    }

    .player-badges {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .player-badge {
        height: 80px;
    }
}
</style>
